import { useCallback, useState } from "react";

export type ChatMessage = {
  role: "USER" | "ASSISTANT" | "SYSTEM";
  content: string;
};

export type SendMessages = (messages: ChatMessage[]) => Promise<string | null>;

export const useChatHistory = ({
  documentName,
  sendMessages,
  initialPrompt,
}: {
  documentName: string;
  sendMessages: SendMessages;
  initialPrompt?: string;
}) => {
  const [prompt, setPrompt] = useState(initialPrompt?.trim() ?? "");
  const [userMessages, setUserMessages] = useState<string[]>([]);
  const [completedAgentTaskTexts, setCompletedAgentTaskTexts] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSend = useCallback(async () => {
    const trimmed = prompt.trim();
    if (!trimmed || isLoading) return;

    const updatedUserMessages = [...userMessages, trimmed];
    setUserMessages(updatedUserMessages);
    setPrompt("");
    setIsLoading(true);

    const messages: ChatMessage[] = [];
    for (let i = 0; i < updatedUserMessages.length; i++) {
      if (i === updatedUserMessages.length - 1) {
        messages.push({
          role: "USER" as const,
          content: `Remember: you are handling requests for the document ${documentName}?`,
        });
      }
      messages.push({ role: "USER" as const, content: updatedUserMessages[i] });
      if (completedAgentTaskTexts[i]) {
        messages.push({ role: "ASSISTANT" as const, content: completedAgentTaskTexts[i] });
      }
    }

    const responseText = await sendMessages(messages);
    if (responseText !== null) {
      setCompletedAgentTaskTexts((prev) => [...prev, responseText]);
    }

    setIsLoading(false);
  }, [
    prompt,
    isLoading,
    userMessages,
    completedAgentTaskTexts,
    documentName,
    sendMessages,
  ]);

  return {
    userMessages,
    completedAgentTaskTexts,
    prompt,
    setPrompt,
    isLoading,
    handleSend,
  };
};
